// Finar İ9 — form sonunda "← Önceki bölüm / Sonraki bölüm →" + bölüm değişince form
// panelini (kaydırılan <main>) başa alır. Eskiden `window.scrollTo` çağrılıyordu ama
// kaydırma <main overflowY:auto> içinde olduğundan form başa dönmüyordu. Kaydırma anlık
// (smooth değil): MissingPanel'in gecikmeli `scrollIntoView`'u bunun ardından çalışır.
import { useEffect, useRef } from "react";
import { adjacentSections, SECTION_PAGER_COPY, type PagerSection } from "../../lib/section-pager";

interface Props {
  sections: readonly PagerSection[];
  activeSectionId: string | null;
  onSelect: (sectionId: string) => void;
  /** false → düğmeler gizlenir (ör. köken çipi aktifken düz liste); kaydırma yine çalışır. */
  showButtons?: boolean;
}

const buttonStyle = {
  display: "flex",
  flexDirection: "column" as const,
  gap: 2,
  maxWidth: "48%",
  padding: "10px 14px",
  borderRadius: 8,
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  color: "var(--color-primary)",
  fontSize: "0.85rem",
  fontWeight: 600,
  cursor: "pointer",
};

const subStyle = {
  color: "var(--color-text-3)",
  fontSize: "0.75rem",
  fontWeight: 400,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
};

export function SectionPager({
  sections,
  activeSectionId,
  onSelect,
  showButtons = true,
}: Props): JSX.Element {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const scroller = ref.current?.closest("main");
    if (scroller && typeof scroller.scrollTo === "function") scroller.scrollTo({ top: 0 });
  }, [activeSectionId]);

  const { prev, next } = adjacentSections(sections, activeSectionId);
  const visible = showButtons && (prev !== null || next !== null);

  return (
    <nav
      ref={ref}
      aria-label={SECTION_PAGER_COPY.navLabel}
      style={{
        display: visible ? "flex" : "none",
        justifyContent: "space-between",
        gap: 12,
        maxWidth: 820,
        margin: "24px auto 0 auto",
      }}
    >
      {prev ? (
        <button
          type="button"
          // Erişilebilir ad yalnız "Önceki bölüm" — bölüm adı ada girerse kenar çubuğundaki
          // bölüm düğmesiyle aynı adı taşır (e2e 13 strict mode: iki düğme).
          aria-label={SECTION_PAGER_COPY.prev}
          onClick={() => onSelect(prev.id)}
          style={buttonStyle}
        >
          <span aria-hidden="true">{SECTION_PAGER_COPY.prev}</span>
          <span aria-hidden="true" style={subStyle}>
            {prev.label}
          </span>
        </button>
      ) : (
        <span />
      )}
      {next ? (
        <button
          type="button"
          aria-label={SECTION_PAGER_COPY.next}
          onClick={() => onSelect(next.id)}
          style={{ ...buttonStyle, alignItems: "flex-end", textAlign: "right" }}
        >
          <span aria-hidden="true">{SECTION_PAGER_COPY.next}</span>
          <span aria-hidden="true" style={subStyle}>
            {next.label}
          </span>
        </button>
      ) : null}
    </nav>
  );
}
